import type { ChatModelAdapter, ChatModelRunResult } from '@assistant-ui/react'
import type { ChatService } from './chat-service'
import {
  detectClientDeterministicIntent,
  MockChatService,
  type SingleActionIntentResponse,
} from './mock-chat-service'
import { GatewayChatService } from './gateway-chat-service'
import { useStudioStore } from '@/store/studio-store'
import { mcpBus } from '@/mcp/studio-mcp-bus'
import { STUDIO_NS, RUNTIME_NS, type McpResult } from '@/mcp/types'
import { runtimeBridge } from '@/mcp/runtime-bridge'
import {
  createStudioRegistry,
  subscribeStudioMcpToolsToRegistry,
  syncStudioMcpToolsIntoRegistry,
} from '@/agent/webmcp-setup'
import { enrichChatContext } from './context-enricher'
import { buildOntologyContextString } from './ontology-helper'
import { buildPageContext, formatContextInstructions } from './chat-context'
import { buildCogoAppChatContext } from './cogo-app-context'
import { buildStudioChatModelPolicy } from './chat-model-policy'
import { buildStudioProjectReadinessChatContext } from './project-readiness-chat-context'
import {
  formatStudioProjectReadinessChatResponse,
  isStudioProjectReadinessQuestion,
} from './project-readiness-chat-response'
import { surfaceStudioCapabilityGatewayResult } from './studio-capability-result-surface'
import { detectLiveCogoSpecIntent } from './live-cogo-spec-intent'
import { useEditorStore } from '@/features/page-editor/store/editor-store'
import { withCogoDevProxyHeaderRecord } from '@/services/cogo-platform-config'
import { resolveChatGatewayBase } from './chat-gateway-config'
import {
  CHAT_RUNTIME_UNREACHABLE_SENTENCE,
  chatRuntimeRefusedSentence,
  resolveChatRuntimeUrl,
} from './chat-runtime-config'
import { listStudioChatOperationContracts } from './studio-chat-operation-registry'
import { detectApplicationImportIntent } from './application-import-intent'
import { detectUpdateExistingWorkIntent } from './update-existing-work-intent'
import { chatToolFailureMessage } from './chat-tool-failure-copy'
import { STUDIO_DICTIONARIES } from '@/i18n/studio-dictionaries'
import { getLocale } from '@/pages/cogo-marketing/i18n/locale-store'
import { getCurrentThreadId } from '@/store/selectors'
import type { CliAction } from '@/store/chat-types'
import type { StudioState } from '@/store/studio-store'
import { cogoXyzRegistry } from '@/features/cogo-xyz/cogo-xyz-registry'
import {
  hasExplicitStudioChatMutationNegation,
  hasStudioChatMutationIntent,
} from './studio-chat-mutation-intent'

/** Parse a dotted entityId (e.g. "hero.variables.title") into parts. */
export function parseEntityId(id: string): { blockName: string; category: string; name: string } | undefined {
  const parts = id.split('.')
  if (parts.length < 3) return undefined
  return { blockName: parts[0], category: parts[1], name: parts.slice(2).join('.') }
}

// ─── Service instances ─────────────────────────────────────
const mockService = new MockChatService(0)

const gatewayService = new GatewayChatService({
  getGatewayUrl: () => resolveChatGatewayBase(),
  getJwt: () => useStudioStore.getState().session?.access_token,
  getProjectId: () => {
    const state = useStudioStore.getState()
    return state.currentProjectId ?? getCurrentThreadId(state)
  },
  getAnonKey: () => import.meta.env.VITE_SUPABASE_ANON_KEY || '',
})

let studioWebMcpRegistry: ReturnType<typeof createStudioRegistry> | null = null

function getStudioWebMcpRegistry(): ReturnType<typeof createStudioRegistry> {
  if (!studioWebMcpRegistry) {
    studioWebMcpRegistry = createStudioRegistry()
    subscribeStudioMcpToolsToRegistry(studioWebMcpRegistry)
  }
  syncStudioMcpToolsIntoRegistry(studioWebMcpRegistry)
  return studioWebMcpRegistry
}

/** Decide which ChatService to use per-request */
function getService(): ChatService {
  if (import.meta.env.VITE_E2E_MOCK === 'true') return mockService
  if (import.meta.env.VITE_CHAT_MOCK === 'true') return mockService
  return gatewayService
}

/**
 * Phase 032 D-06 REQ-BASE-07 — operator-visible adapter label.
 *
 * Mirrors `getService()` selector logic exactly so any UI surface that wants
 * to display the active chat service (e.g. the chat-llm-badge in
 * `StudioComposer`) reads the same explicit build-flag checks.
 *
 * Mock chat is available only under an explicit build flag. Missing runtime
 * configuration or authentication must fail through the gateway instead of
 * silently generating a deterministic fixture.
 */
export function getActiveChatServiceLabel(): 'mock' | 'gateway' {
  if (import.meta.env.VITE_E2E_MOCK === 'true') return 'mock'
  if (import.meta.env.VITE_CHAT_MOCK === 'true') return 'mock'
  return 'gateway'
}

// ─── Runtime URL (AG-UI SSE) ────────────────────────────────
/**
 * The AG-UI SSE runtime this build talks to, or `null` when it runs none.
 *
 * Reads `VITE_COPILOTKIT_URL` ONLY. It used to read
 * `VITE_CHAT_GATEWAY_URL || VITE_COPILOTKIT_URL`, which handed this consumer
 * a value the gateway consumer appends `/api/chat-gateway` to — see
 * `chat-runtime-config.ts` for the measurement. `null` sends the turn down
 * the gateway transport below; it is not a refusal.
 */
function getRuntimeUrl(): string | null {
  return resolveChatRuntimeUrl()
}

function getLastUserText(messages: readonly any[]): string {
  const lastUserMsg = [...messages].reverse().find((m: any) => m.role === 'user')
  return lastUserMsg?.content
    ?.filter((p: any) => p.type === 'text')
    .map((p: any) => p.text)
    .join('') ?? ''
}

export async function executeCliAction(action: CliAction): Promise<McpResult> {
  // TOTAL by contract: every failure below is a returned `McpResult`, never a
  // throw, because the caller narrates the turn from the result. A throw
  // escaping here took the whole turn with it — no authored text, no notice,
  // just a rejected generator (review "Minor", 2026-08-13).
  //
  // The seams that are NOT already guarded, and where they sit relative to the
  // command actually running, since that is what decides whether "…so nothing
  // was changed" is true of a caught throw:
  //   • `runtimeBridge.execute` — `crypto.randomUUID()` (absent outside a
  //     secure context) and `postMessage` (DataCloneError on a non-cloneable
  //     payload) both throw BEFORE anything is sent;
  //   • `mcpBus` middleware — the chain is awaited unwrapped. The only
  //     middleware registered today (undo, init-undo.ts) has no throw path,
  //     but a future one throwing AFTER its `next()` would be a post-success
  //     throw, and mapping that to the failure copy would assert something
  //     false. Guard such work inside the middleware.
  // `registry.callTool` and `notificationBus.emit` already guard themselves.
  try {
    return await executeCliActionInner(action)
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : String(error),
    }
  }
}

async function executeCliActionInner(action: CliAction): Promise<McpResult> {
  // Orchestration identity is deliberately kept outside `params`. Raw runtime
  // and WebMCP tools must receive only the model-declared operation payload.
  if (action.orchestration && (!action.orchestration.runId.trim() || !action.orchestration.toolCallId.trim())) {
    return {
      success: false,
      error: 'AG-UI tool execution requires non-empty runId and toolCallId values.',
    }
  }
  const params = action.payload
  const isRuntime = action.type.startsWith('runtime.') || action.type.startsWith(RUNTIME_NS)
  if (isRuntime) {
    const rtTool = action.type.startsWith(RUNTIME_NS)
      ? action.type
      : action.type.startsWith('runtime.')
        ? `cogo.${action.type}`
        : `${RUNTIME_NS}${action.type}`
    return runtimeBridge.execute({ tool: rtTool, params })
  }
  const studioTool = normalizeStudioToolName(action.type)
  if (studioTool) {
    const contextualOperation = useStudioStore.getState().chatContext.operationContract
    const registeredOperation = listStudioChatOperationContracts().find(
      (operation) =>
        studioTool === operation.executeTool.name ||
        studioTool === operation.webMcpToolName,
    )
    const operation =
      registeredOperation ??
      (contextualOperation &&
      (studioTool === contextualOperation.executeTool.name ||
        studioTool === contextualOperation.webMcpToolName)
        ? contextualOperation
        : undefined)
    if (
      operation
    ) {
      const gatewayResult = await mcpBus.execute({
        tool: 'cogo.studio.capability.executeOperation',
        params: { operationId: operation.id, params,
          ...(action.orchestration ? { orchestration: action.orchestration } : {}),
        },
      })
      return surfaceStudioCapabilityGatewayResult(gatewayResult)
    }
    const manifestEntry = mcpBus
      .getManifest()
      .find((entry) => entry.name === studioTool)
    if (manifestEntry?.metadata?.readOnly !== true) {
      return {
        success: false,
        error: `Studio action requires an operation contract before Chat can execute it: ${studioTool}`,
      }
    }
    const result = await mcpBus.executeWebMcp({ tool: studioTool, params })
    if (result.success && shouldSyncPreviewPageAfterAction(action)) syncPreviewPageAfterAction(action)
    return result
  }
  if (isFullyQualifiedCogoToolName(action.type)) {
    return executeStudioWebMcpTool(action.type, params)
  }
  return {
    success: false,
    error: `Unsupported Studio action: ${action.type}`,
  }
}

function normalizeStudioToolName(actionType: string): string | null {
  if (actionType.startsWith(STUDIO_NS)) return actionType
  if (isFullyQualifiedCogoToolName(actionType)) return null
  return `${STUDIO_NS}${actionType}`
}

function shouldSuppressModelMutation(
  action: CliAction,
  userText: string,
): boolean {
  if (
    !hasExplicitStudioChatMutationNegation(userText) ||
    hasStudioChatMutationIntent(userText)
  ) {
    return false
  }

  const studioTool = normalizeStudioToolName(action.type)
  if (!studioTool) return false

  const operation = listStudioChatOperationContracts().find(
    (candidate) =>
      studioTool === candidate.executeTool.name ||
      studioTool === candidate.webMcpToolName,
  )
  return operation?.readOnly === false
}

async function executeStudioWebMcpTool(
  tool: string,
  params: Record<string, unknown>,
): Promise<McpResult> {
  try {
    const data = await getStudioWebMcpRegistry().callTool(tool, params)
    return { success: true, data }
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : String(error),
    }
  }
}

function isFullyQualifiedCogoToolName(tool: string): boolean {
  return /^cogo\.[a-z0-9_.-]+$/i.test(tool)
}

function shouldSyncPreviewPageAfterAction(action: { type: string; payload?: unknown }): boolean {
  const type = action.type.startsWith(STUDIO_NS)
    ? action.type.slice(STUDIO_NS.length)
    : action.type
  return type === 'applyAppPatch'
}

function syncPreviewPageAfterAction(action: { type: string; payload?: unknown }): void {
  const payload = (action.payload ?? {}) as Record<string, unknown>
  const store = useStudioStore.getState()
  const pages = store.currentProject?.manifest?.pages ?? []
  if (pages.length === 0) return

  const explicitPageId = typeof payload.selectPageId === 'string' ? payload.selectPageId : null
  const patchPageId = action.type === 'applyAppPatch' ? findPageIdFromPatchPayload(payload) : null
  const targetPageId = explicitPageId ?? patchPageId ?? resolveExistingCurrentPageId(store.currentPageId, pages)
  if (!targetPageId) return

  store.setCurrentPageId?.(targetPageId)
  store.setViewMode?.('preview')
  store.setActiveRailItem?.('preview')
  if (useEditorStore.getState().app?.pages?.some((page) => page.id === targetPageId)) {
    useEditorStore.getState().setActivePage(targetPageId)
  }
}

function findPageIdFromPatchPayload(payload: Record<string, unknown>): string | null {
  const patches = Array.isArray(payload.patches) ? payload.patches : []
  for (let index = patches.length - 1; index >= 0; index -= 1) {
    const patch = patches[index] as Record<string, unknown> | null
    if (!patch || patch.op !== 'add') continue
    if (typeof patch.path !== 'string' || !patch.path.startsWith('/manifest/pages')) continue
    const value = patch.value as Record<string, unknown> | null
    if (value && typeof value.id === 'string' && value.id) return value.id
  }
  return null
}

function resolveExistingCurrentPageId(
  currentPageId: string | null | undefined,
  pages: Array<{ id?: string }>,
): string | null {
  if (currentPageId && pages.some((page) => page.id === currentPageId)) return currentPageId
  return pages.find((page) => typeof page.id === 'string' && page.id)?.id ?? null
}

function isHandledAppGenerationEcho(text: string): boolean {
  const store = useStudioStore.getState()
  return (
    store.conversation.phase === 'generating' &&
    store.conversation.confirmedNarrative?.trim() === text.trim()
  )
}

/**
 * The creator-readable line for a failed chat-dispatched tool.
 *
 * Production 2026-08-04: every site below used to concatenate the engine's own
 * string into the assistant's message (`[MCP Error: Invalid input for Studio
 * operation …]`). The raw text still exists — it goes to the console, where
 * engineers can read it — but what lands in the thread is authored copy.
 */
function chatToolFailureText(raw: string | null | undefined): string {
  if (raw) console.error('[chat] tool failure:', raw)
  return chatToolFailureMessage(
    raw,
    STUDIO_DICTIONARIES[getLocale()].chat.toolFailure,
  )
}

function chatWebMcpVerificationText(target?: string): string {
  return STUDIO_DICTIONARIES[getLocale()].chat.webMcpVerified(target)
}

function chatReadOnlyCompletionFallbackText(): string {
  return STUDIO_DICTIONARIES[getLocale()].chat.readOnlyCompletionFallback
}

const REQUESTS_REVIEW_STATUS_TOPIC =
  /(?:requests?\s*(?:&|and)?\s*reviews?|active\s+requests?|request\s+status)|(?:요청·?검토|진행\s*중인\s*요청|요청\s*상태)|(?:依頼とレビュー|進行中の依頼|依頼状況)|(?:请求与审核|进行中的请求|请求状态)|(?:請求與審閱|進行中的請求|請求狀態)/i

function isRequestsReviewStatusQuestion(
  text: string,
  state: StudioState,
): boolean {
  return (
    state.chatContext.surfaceId === 'requests-review' &&
    REQUESTS_REVIEW_STATUS_TOPIC.test(text) &&
    !hasStudioChatMutationIntent(text)
  )
}

function requestsReviewStatusText(state: StudioState): string {
  const copy = STUDIO_DICTIONARIES[getLocale()].requests
  const updates = state.chatMessages
    .filter((message) => message.kind === 'agent-worklog')
    .slice(-5).length
  const checks = cogoXyzRegistry.snapshot().toolRuns.slice(-6)
  const approvals = checks.filter(
    (run) =>
      run.status === ('approval_required' as typeof run.status) ||
      run.output?.status === 'approval_required',
  ).length
  const heading = updates === 0 && checks.length === 0
    ? copy.timeline.emptyTitle
    : copy.current.title
  return `${heading} — ${copy.tiles.updates} ${updates} · ${copy.tiles.checks} ${checks.length} · ${copy.tiles.needsApproval} ${approvals}`
}

export function isCurrentContextInspection(text: string): boolean {
  if (/\bcogo\.nav\.currentContext\b/i.test(text)) return true

  // Only a standalone location/identity request belongs to the deterministic
  // readback. Merely mentioning "현재 화면" inside a broader analysis must
  // keep flowing to the project-aware model.
  const firstClause = text.split(/[.!?。！？]/, 1)[0]?.trim() ?? ''
  return /^(?:(?:what|which)\s+page\b|where\s+am\s+i\b|현재\s*(?:페이지|화면)(?:을|를|이|은)?\s*(?:확인|알려|어디|무엇)|어떤\s*(?:페이지|화면)(?:인지)?\s*(?:확인|알려)|어디(?:에|를)?\s*(?:있는|보고))/i.test(
    firstClause,
  )
}

async function* runCurrentContextInspection(): AsyncGenerator<ChatModelRunResult> {
  const result = await executeCliAction({
    type: 'cogo.nav.currentContext',
    payload: {},
  })
  if (!result.success) {
    yield {
      content: [{
        type: 'text' as const,
        text: chatToolFailureText(result.error),
      }],
    }
    return
  }

  const data = result.data as {
    page?: { id?: string; name?: string; surface?: string } | null
    project?: { name?: string } | null
    shell?: string
  } | undefined
  const page = data?.page
  const verifiedTarget = page?.name
    ? `${page.name}${page.id ? ` (${page.id})` : ''}`
    : data?.project?.name ?? data?.shell
  yield {
    content: [{
      type: 'text' as const,
      text: chatWebMcpVerificationText(verifiedTarget),
    }],
  }
}

async function* runClientDeterministicIntent(
  intent: SingleActionIntentResponse,
  abortSignal: AbortSignal,
): AsyncGenerator<ChatModelRunResult> {
  // Browser finding, 2026-08-04. A deterministic intent whose action opens a
  // project ends the turn in a DIFFERENT chat thread than it started in:
  // `getCurrentThreadId` flips 'account-global' → `ide-<projectId>`, and
  // `AssistantRuntimeProvider key={currentThreadId}` (App.tsx) remounts on that
  // flip — which discards the in-memory transcript this reply was written to.
  // Measured on the update-existing-work chip: the project opened and the
  // assistant's "Opening …" was never painted.
  //
  // Thread separation is deliberate (IA-3: a dashboard conversation must not
  // bleed into a project workspace), so the transcript is not what should
  // change. Only the ONE reply that crossed the boundary needs carrying, and
  // `pendingChatNotice` is that surface — rendered by the chat panel on both
  // sides of the switch, dismissable, and already the channel
  // `surfaceStudioGenerationFailure` and `openStudioLaunchChat` use.
  const threadBefore = getCurrentThreadId(useStudioStore.getState())

  // ONE action, so ONE outcome, so one sentence can be true about it.
  //
  // This was a `for` loop over `intent.actions` until 2026-08-13, and the loop
  // was wrong for every length it accepted but one. Review reproductions:
  //
  //   I-4  two actions, the first succeeding and the second failing — the
  //        turn's single answer was the failure copy ("…so nothing was
  //        changed") over an action that had already landed;
  //   I-5  three actions, the first flipping the thread and aborting in the
  //        same tick — the carried notice read `Added "…" as a new screen and
  //        opened the app.` while actions 2 and 3 never ran.
  //
  // Both are F12 with the sign flipped: an answer derived from one side of the
  // result set. With one sentence and no per-action copy there is no truthful
  // rendering of a partial run, so `SingleActionIntentResponse` makes the
  // longer shape uncompilable at every producer instead
  // (single-action-intent.contract.ts) and the answer here is a total function
  // of one result.
  const actions = intent.actions ?? []

  // Defence in depth for a contract TypeScript cannot carry past compile time.
  //
  // `SingleActionIntentResponse` makes the two-action shape uncompilable at
  // every producer, and that binds today (widening it kills 5 of the 6
  // `@ts-expect-error` pins in single-action-intent.contract.ts). But types are
  // erased: a cast, a loosely typed future producer, or a value from untyped
  // code can still arrive here with two. Destructuring alone then dropped
  // action 1 on the floor and reported the intent's full success — this
  // slice's own defect, back as a SILENT DROP instead of a wrong sentence.
  //
  // Deliberately NOT a `throw`. This generator feeds the assistant runtime; an
  // exception out of it kills the turn with no authored text, which is the
  // defect fixed in the same lane (`executeCliAction` made total, post-answer
  // effects guarded). "Loud" for an engineer is the console; "loud" for a
  // reader still has to be a sentence. So the turn is REFUSED: nothing runs,
  // the diagnostic is logged, and the authored failure line is true precisely
  // because the refusal happens before the first action.
  if (actions.length > 1) {
    const answer = chatToolFailureText(
      `runClientDeterministicIntent received ${actions.length} actions ` +
        `(${actions.map((a) => a.type).join(', ')}); a deterministic turn ` +
        'narrates one action with one sentence, so nothing was dispatched.',
    )
    yield { content: [{ type: 'text' as const, text: answer }] }
    return
  }

  const [action] = actions

  // Nothing to run IS its own outcome — a clarification, a question, a
  // summary. Nothing can contradict it later, so it is said now.
  if (!action) {
    yield { content: [{ type: 'text' as const, text: intent.text }] }
    if (abortSignal.aborted) return
    await applyClientDeterministicIntentEffects(intent)
    return
  }

  // Browser finding, 2026-08-13 (F12,
  // docs/studio/chat-ux-sweep-2026-08-13-ide-chat-surfaces.md). This used to
  // yield `intent.text` BEFORE the action ran and then APPEND the failure line
  // to it, so one turn rendered, together:
  //
  //     Added "Add A Login" as a new screen and opened the app.
  //     이 변경에 필요한 내용이 요청에 없어서 아무것도 바꾸지 않았어요. …
  //
  // while the patch had errored (`input.selectPageId is not allowed`) and the
  // block list was still empty. The success sentence is the specific one, and
  // it was written before there was anything to know. Running the action
  // BEFORE the first `yield` is also strictly safer for the carry below: this
  // generator is lazy, so everything after a `yield` runs only if the consumer
  // pulls again, and the remount that discards the transcript abandons that
  // consumer. Everything up to the first `yield` runs inside the `next()` the
  // consumer is already awaiting.
  const result = await executeCliAction(action)
  const answer = result.success ? intent.text : chatToolFailureText(result.error)

  // Carry the reply the INSTANT the thread changes — before the abort check,
  // before any yield. The remount that discards the transcript also aborts
  // this run, so a carry placed after the abort check is never reached
  // (measured: it cost `update-existing-work-chip.spec.ts` its notice). What
  // is carried is the answer, so a turn that failed cannot be remembered as a
  // success on the other side of the switch either.
  if (getCurrentThreadId(useStudioStore.getState()) !== threadBefore) {
    useStudioStore.getState().setPendingChatNotice(answer)
  }
  if (abortSignal.aborted) return

  yield { content: [{ type: 'text' as const, text: answer }] }
  await applyClientDeterministicIntentEffects(intent)
}

/**
 * The non-message effects of an intent, applied AFTER its answer is given.
 *
 * Guarded, and deliberately not reported: these are navigation conveniences
 * (switch workspace mode, scroll to a section), not the thing the turn is
 * about. A throw here used to reject the generator and take the answer's turn
 * with it — the answer had been yielded, but the consumer saw an error instead
 * of a completed run (review "Minor", 2026-08-13; found by the turn-outcome
 * test, whose intent carries `targetMode: 'page'`). Turning a failed scroll
 * into "…so nothing was changed" would be the opposite mistake, so the failure
 * goes to the console and the answer stands.
 */
async function applyClientDeterministicIntentEffects(
  intent: SingleActionIntentResponse,
): Promise<void> {
  try {
    if (intent.targetMode) {
      await mcpBus.execute({
        tool: `${STUDIO_NS}setMode`,
        params: { mode: intent.targetMode },
      })
    }
    if (intent.scrollTarget) {
      useStudioStore.getState().setScrollTarget(intent.scrollTarget)
    }
  } catch (error) {
    console.error('[chat] post-answer intent effect failed:', error)
  }
}


// ─── Gateway / Mock Adapter (ChatService interface) ─────────
function buildStudioChatContext(
  store: StudioState,
  lastUserText: string,
) {
  const rawContext = {
    ...store.chatContext,
    phase: store.conversation.phase,
    appName: store.conversation.appName ?? undefined,
    narrative: store.conversation.confirmedNarrative ?? undefined,
    goals: store.conversation.confirmedGoals.length > 0 ? store.conversation.confirmedGoals : undefined,
    scenarios: store.story?.bddScenarios,
    pages: store.story?.pages,
    // Data tab context
    dataCategory: store.mode === 'data' ? store.dataCategory : undefined,
    selectedEntity: store.mode === 'data' && store.selectedEntityId
      ? parseEntityId(store.selectedEntityId)
      : undefined,
    // Ontology context for LLM understanding of app structure
    ontologyContext: [
      buildOntologyContextString(store),
      store.chatContext.ontologyContext,
      store.chatContext.chatOperationInstructions,
    ].filter(Boolean).join('\n'),
    // Complete bounded Coogo JSON context for app-level CRUD patches.
    cogoAppContext: buildCogoAppChatContext(store.currentProject),
    // Read-only status authority. The complete app JSON above is useful for
    // edits but cannot prove generation, ActionFlow, or publish readiness.
    // This projection uses the exact gate and plan precedence of Publish.
    projectReadinessContext: buildStudioProjectReadinessChatContext(store),
    // Phase 4 IA-4 — auto-injected page context (shell + route + project +
    // block) so the LLM can answer "what page am I on?" without guessing.
    pageContextInstructions: formatContextInstructions(buildPageContext(store)),
    chatModelPolicy: buildStudioChatModelPolicy('studio-chat'),
  }
  return enrichChatContext(rawContext, lastUserText)
}

export function buildSseChatState(
  store: StudioState,
  lastUserText: string,
) {
  return {
    ...buildStudioChatContext(store, lastUserText),
    story: store.story,
  }
}

export function buildSseToolDefinitions() {
  return getStudioWebMcpRegistry().getManifest().map((tool) => ({
    name: toProviderSafeToolName(tool.name),
    description: tool.description,
    parameters: tool.inputSchema,
  }))
}

function toProviderSafeToolName(name: string): string {
  return name.replace(/[^a-zA-Z0-9_-]/g, '_')
}

export function resolveSseToolCall(
  name: string,
  params: Record<string, unknown>,
): { name: string; params: Record<string, unknown> } {
  if (
    name === 'cogo_nav' &&
    (params.currentContext === true || params.currentContext === 'true')
  ) {
    return { name: 'cogo.nav.currentContext', params: {} }
  }

  const manifestTool = getStudioWebMcpRegistry()
    .getManifest()
    .find(
      (tool) =>
        tool.name === name || toProviderSafeToolName(tool.name) === name,
    )
  if (manifestTool) return { name: manifestTool.name, params }

  if (name === 'cogo_patch_app') {
    return { name: `${STUDIO_NS}applyAppPatch`, params }
  }
  if (name.startsWith(RUNTIME_NS) || isFullyQualifiedCogoToolName(name)) {
    return { name, params }
  }

  const stripped = name.replace(/^cogo_/, '')
  const camelCase = stripped.replace(/_([a-z])/g, (_, c) => c.toUpperCase())
  return { name: `${STUDIO_NS}${camelCase}`, params }
}

async function* runChatServiceAdapter(
  messages: readonly any[],
  abortSignal: AbortSignal,
): AsyncGenerator<ChatModelRunResult> {
  const service = getService()
  const store = useStudioStore.getState()
  const lastUserText = getLastUserText(messages)
  const context = buildStudioChatContext(store, lastUserText)

  // Convert assistant-ui messages to plain {role, content} for ChatService
  const chatMessages = messages.map((m: any) => ({
    role: m.role ?? 'user',
    content:
      m.content
        ?.filter((p: any) => p.type === 'text')
        .map((p: any) => p.text)
        .join('') ?? '',
  }))

  let fullText = ''
  let suppressedReadOnlyMutation = false

  for await (const chunk of service.sendMessage(chatMessages, context)) {
    if (abortSignal.aborted) return

    switch (chunk.type) {
      case 'text':
        fullText += chunk.content
        yield { content: [{ type: 'text' as const, text: fullText }] }
        break
      case 'actions':
        // Route each action through the appropriate bus based on namespace
        for (const action of chunk.actions) {
          if (shouldSuppressModelMutation(action, lastUserText)) {
            suppressedReadOnlyMutation = true
            continue
          }
          const result = await executeCliAction(action)
          if (!result.success) {
            fullText += `\n\n${chatToolFailureText(result.error)}`
            yield { content: [{ type: 'text' as const, text: fullText }] }
          }
        }
        break
      case 'mode':
        // Route mode changes through mcpBus
        await mcpBus.execute({
          tool: `${STUDIO_NS}setMode`,
          params: { mode: chunk.mode },
        })
        break
      case 'scroll':
        useStudioStore.getState().setScrollTarget(chunk.target)
        break
      case 'error':
        fullText += `\n\n⚠ ${chunk.message}`
        yield { content: [{ type: 'text' as const, text: fullText }] }
        break
      case 'validation': {
        const validationData = {
          blocks: [{
            name: chunk.blockName,
            score: chunk.score,
            grade: chunk.grade,
            issues: chunk.issues,
          }],
        }
        const validationBlock = '```cogo-validation\n' + JSON.stringify(validationData) + '\n```'
        fullText += '\n\n' + validationBlock
        yield { content: [{ type: 'text' as const, text: fullText }] }
        break
      }
      case 'composite': {
        // D-08: Do NOT dispatch. Embed as cogo-composite code block for ApprovalCard.
        const compositeData = { actions: chunk.actions, preview: chunk.preview }
        const compositeBlock = '```cogo-composite\n' + JSON.stringify(compositeData) + '\n```'
        fullText += '\n\n' + compositeBlock
        yield { content: [{ type: 'text' as const, text: fullText }] }
        break
      }
    }
  }

  if (suppressedReadOnlyMutation && !fullText.trim()) {
    yield {
      content: [{
        type: 'text' as const,
        text: chatReadOnlyCompletionFallbackText(),
      }],
    }
  }
}

// ─── AG-UI Event Types ─────────────────────────────────────
const AG = {
  TEXT_MESSAGE_CONTENT: 'TEXT_MESSAGE_CONTENT',
  TOOL_CALL_START: 'TOOL_CALL_START',
  TOOL_CALL_ARGS: 'TOOL_CALL_ARGS',
  TOOL_CALL_END: 'TOOL_CALL_END',
  RUN_ERROR: 'RUN_ERROR',
} as const

// ─── SSE Adapter (production) ──────────────────────────────
async function* runSSEAdapter(
  runtimeUrl: string,
  messages: readonly any[],
  abortSignal: AbortSignal,
): AsyncGenerator<ChatModelRunResult> {
  const store = useStudioStore.getState()
  const lastUserText = getLastUserText(messages)
  const accessToken = store.session?.access_token
  const runId = crypto.randomUUID()
  // A transport failure must leave the turn with an authored line, never a
  // rejected generator. The sibling transport (`runChatServiceAdapter` ->
  // `gateway-chat-service.ts:111-126`) already guards its fetch and yields
  // `{type:'error'}`; this one did not, so `TypeError: Failed to fetch`
  // propagated out of `cogoChatAdapter.run`. assistant-ui marks the message
  // `incomplete/error` and rethrows, its `Thread` renders no error component
  // for that state, and `studio-composer.tsx` does not await the `append` it
  // rejects — so the send produced no error, no notice and no bubble.
  // Measured live on the appliance 2026-08-26.
  //
  // `abortSignal` is deliberately NOT swallowed: a user-cancelled turn is not
  // a failure to report, and narrating one would be asserting something false.
  let res: Response
  try {
    res = await fetch(runtimeUrl, {
      method: 'POST',
      headers: withCogoDevProxyHeaderRecord({
        'Content-Type': 'application/json',
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      }),
      body: JSON.stringify({
        threadId: crypto.randomUUID(),
        runId,
        toolExecutionMode: 'client-delegated',
        messages: messages.map((m: any) => ({
          role: m.role ?? 'user',
          content:
            m.content
              ?.filter((p: any) => p.type === 'text')
              .map((p: any) => p.text)
              .join('') ?? '',
        })),
        tools: buildSseToolDefinitions(),
        state: buildSseChatState(store, lastUserText),
      }),
      signal: abortSignal,
    })
  } catch (error) {
    if (abortSignal.aborted) return
    console.error('[chat-runtime] fetch failed:', error)
    yield {
      content: [
        { type: 'text' as const, text: CHAT_RUNTIME_UNREACHABLE_SENTENCE },
      ],
    }
    return
  }

  if (!res.ok || !res.body) {
    // Was `Error: ${res.status}` — an integrator's sentence, in English, in a
    // creator's chat column. The status survives because it is the part an
    // operator reading a screenshot can act on.
    yield {
      content: [
        {
          type: 'text' as const,
          text: chatRuntimeRefusedSentence(res.status),
        },
      ],
    }
    return
  }

  const reader = res.body.getReader()
  const decoder = new TextDecoder()
  let text = ''
  let suppressedReadOnlyMutation = false
  const toolCalls = new Map<string, { id: string; name: string; args: string }>()
  let buffer = ''

  while (true) {
    const { done, value } = await reader.read()
    if (done || abortSignal.aborted) break

    buffer += decoder.decode(value, { stream: true })
    const blocks = buffer.split('\n\n')
    buffer = blocks.pop() ?? ''

    for (const block of blocks) {
      for (const line of block.split('\n')) {
        if (!line.startsWith('data: ')) continue
        let event: any
        try {
          event = JSON.parse(line.slice(6))
        } catch {
          continue
        }

        switch (event.type) {
          case AG.TEXT_MESSAGE_CONTENT:
            text += event.delta ?? ''
            break
          case AG.TOOL_CALL_START:
            toolCalls.set(event.toolCallId, {
              id: event.toolCallId,
              name: event.toolCallName ?? '',
              args: '',
            })
            break
          case AG.TOOL_CALL_ARGS: {
            const tc = toolCalls.get(event.toolCallId)
            if (tc) tc.args += event.delta ?? ''
            break
          }
          case AG.TOOL_CALL_END: {
            const tc = toolCalls.get(event.toolCallId)
            if (tc) {
              const params = parseToolArgs(tc.args)
              const resolved = resolveSseToolCall(tc.name, params)
              tc.name = resolved.name
              tc.args = JSON.stringify(resolved.params)
              const action: CliAction = {
                type: resolved.name,
                payload: resolved.params,
                orchestration: { runId, toolCallId: tc.id },
              }
              if (shouldSuppressModelMutation(action, lastUserText)) {
                suppressedReadOnlyMutation = true
                toolCalls.delete(tc.id)
                break
              }
              const result = await executeCliAction({
                ...action,
              })
              if (
                resolved.name === 'cogo.nav.currentContext' &&
                result.success
              ) {
                const data = result.data as {
                  page?: { name?: string; id?: string; surface?: string } | null
                } | undefined
                const page = data?.page
                const target = page?.name
                  ? `${page.name}${page.id ? ` (${page.id})` : ''}`
                  : undefined
                text += chatWebMcpVerificationText(target)
              } else if (!result.success) {
                text += `\n${chatToolFailureText(result.error ?? resolved.name)}`
              }
            }
            break
          }
          case AG.RUN_ERROR:
            text += `\n${chatToolFailureText(event.message)}`
            break
        }

        // Yield accumulated content
        const content: any[] = []
        if (text) content.push({ type: 'text', text })
        for (const tc of toolCalls.values()) {
          try {
            content.push({
              type: 'tool-call',
              toolCallId: tc.id,
              toolName: tc.name,
              args: JSON.parse(tc.args || '{}'),
            })
          } catch {
            /* skip malformed */
          }
        }
        if (content.length) yield { content }
      }
    }
  }


  if (suppressedReadOnlyMutation && !text.trim()) {
    yield {
      content: [{
        type: 'text' as const,
        text: chatReadOnlyCompletionFallbackText(),
      }],
    }
  }
}

function parseToolArgs(args: string): Record<string, unknown> {
  try {
    const parsed = JSON.parse(args || '{}')
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? parsed as Record<string, unknown>
      : {}
  } catch {
    return {}
  }
}

// ─── ChatModelAdapter export ───────────────────────────────
export const cogoChatAdapter: ChatModelAdapter = {
  async *run({ messages, abortSignal }) {
    const lastUserText = getLastUserText(messages)

    // A pick list answers ONE question. The moment the user says anything
    // else, that question is over — leaving the rows up would offer a choice
    // to a question no longer on screen. Cleared here, unconditionally, so
    // the only thing that can put rows back is the branch that asks again.
    useStudioStore.getState().setPendingWorkChoice(null)

    if (isHandledAppGenerationEcho(lastUserText)) {
      yield {
        content: [{
          type: 'text' as const,
          text: 'Generating the app now. Preview, ActionFlow, and readiness evidence will update as the build lands.',
        }],
      }
      return
    }

    // Update-existing-work is resolved on the CLIENT, before anything can
    // hand it to a model. Production 2026-08-04: this message reached the
    // gateway as free text and came back "즉시 앱을 생성할게요! 🏦" — a NEW
    // app for a request to change an existing one — then died on an invalid
    // tool input. The workspace read that answers it (`userProjects`) is
    // already in this store, so the lookup never needed a model at all.
    //
    // Placed FIRST among the detectors on purpose: it is the most specific
    // rule here (exact localized chip phrases, anchored at the start), so it
    // cannot shadow a looser one, and running it before the async KG probes
    // keeps an update request off the network entirely.
    const updateExistingWork = detectUpdateExistingWorkIntent(
      lastUserText,
      useStudioStore.getState().userProjects,
      STUDIO_DICTIONARIES[getLocale()].chat.updateExistingWork,
    )
    if (updateExistingWork) {
      // A question the client asked gets rows the client can answer. Set
      // before the reply is yielded so the list and its question paint
      // together; cleared (below, on every run) so it can never outlive the
      // question that produced it.
      useStudioStore
        .getState()
        .setPendingWorkChoice(
          updateExistingWork.choices.length > 0
            ? updateExistingWork.choices.map((choice) => ({
                id: choice.id!,
                name: choice.name,
                updatedOn: choice.updatedOn,
              }))
            : null,
        )
      yield* runClientDeterministicIntent(updateExistingWork.response, abortSignal)
      return
    }

    if (isCurrentContextInspection(lastUserText)) {
      yield* runCurrentContextInspection()
      return
    }

    if (
      isRequestsReviewStatusQuestion(
        lastUserText,
        useStudioStore.getState(),
      )
    ) {
      yield {
        content: [{
          type: 'text' as const,
          text: requestsReviewStatusText(useStudioStore.getState()),
        }],
      }
      return
    }

    if (isStudioProjectReadinessQuestion(lastUserText)) {
      const readiness = buildStudioProjectReadinessChatContext(
        useStudioStore.getState(),
      )
      yield {
        content: [{
          type: 'text' as const,
          text: formatStudioProjectReadinessChatResponse(
            readiness,
            getLocale(),
          ),
        }],
      }
      return
    }

    const applicationImportIntent = detectApplicationImportIntent(lastUserText)
    if (applicationImportIntent) {
      yield* runClientDeterministicIntent(applicationImportIntent, abortSignal)
      return
    }

    const liveCogoSpecIntent = detectLiveCogoSpecIntent(lastUserText)
    if (liveCogoSpecIntent) {
      const transaction = useStudioStore.getState().agenticUxTransaction
      if (transaction?.status === 'running') {
        useStudioStore.getState().advanceAgenticUxTransaction(transaction.id, {
          phase: 'spec-mutating',
          lastSpec: lastUserText,
        })
      }
      yield* runClientDeterministicIntent(liveCogoSpecIntent, abortSignal)
      return
    }

    const deterministicIntent = detectClientDeterministicIntent(lastUserText)
    if (deterministicIntent) {
      yield* runClientDeterministicIntent(deterministicIntent, abortSignal)
      return
    }

    // WS-E chat→knowledge auto-trigger REMOVED (2026-07-24).
    //
    // It inferred a vocabulary contribution from free text and, on a hit,
    // returned a knowledge summary INSTEAD of handling the user's request.
    // Two measured defects made that untenable:
    //   1. False positives on ordinary UI instructions — 4 of 10 realistic
    //      studio messages fired ("이 버튼은 submit으로 처리해줘",
    //      "에러는 toast로 취급해줘", …), because generic Korean verbs
    //      (~로 처리/취급해) are indistinguishable from mapping declarations
    //      and the classifier judged grammar, not term category.
    //   2. A wrong alias does not merely fail to match — it CHANGES asset
    //      selection. Measured: alias 정산→payment made an unrelated
    //      payment asset get selected for "정산 화면 만들어줘" (it is not
    //      selected without the alias). Admitted vocabulary rewrites what a
    //      prompt is understood to be about, so a wrong entry produces
    //      wrong results for every later request, silently.
    //
    // Architecture correction: coogo terminology is DEFINED and held
    // internally (cogo.knowledge-unit.v1 in packages/cogo-validator/ontology
    // — source-anchored via sourceSha256, bound through ontologyRefs,
    // recorded verifiedBy). An LLM may be wrong; RAG may not. So the LLM
    // must never write terminology. When a request is ambiguous the intended
    // resolution is a feedback loop: surface SIMILAR DEFINED terms and let
    // the user choose — the user's selection is the ground truth, and no new
    // vocabulary is minted at runtime.

    const runtimeUrl = getRuntimeUrl()

    if (runtimeUrl) {
      yield* runSSEAdapter(runtimeUrl, messages, abortSignal)
      return
    }

    yield* runChatServiceAdapter(messages, abortSignal)
  },
}
